import { useState } from 'react';

import { motion } from 'framer-motion';
import { CheckIcon, File, FileClock, FileCog, LoaderCircle, XIcon, ShieldAlert } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Permission } from '../../../../../../../backend/src/auth/permissions/permission';
import { DurationEstimate } from '../../../../../../../backend/src/types/types';
import { useSubmitRunApproval } from '@/api/mutations/useSubmitRunApproval';
import { useGetCase } from '@/api/queries/useGetCase';
import { useGetMatter } from '@/api/queries/useGetMatter';
import { useGetUsersOrganisation } from '@/api/queries/useGetUsersOrganisation';
import { Button } from '@/components/atoms/new-button';
import { ConfirmationModal } from '@/components/molecules/Modals/Confirmation';
import { useUserContext } from '@/Contexts/User';

interface WaitingApprovalScreenProps {
  runId: string;
  pageCount?: number;
  onApprovalGranted?: () => void;
  onApprovalDenied?: () => void;
  durationEstimate?: DurationEstimate | null;
}

const WaitingApprovalScreen = ({
  runId,
  pageCount,
  onApprovalGranted,
  onApprovalDenied,
  durationEstimate,
}: WaitingApprovalScreenProps) => {
  const [isContinueLoading, setIsContinueLoading] = useState(false);
  const [isCancelLoading, setIsCancelLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const caseId = searchParams.get('caseId');

  const { data: organisation } = useGetUsersOrganisation();
  const { data: caseData } = useGetCase(caseId ?? '');
  const { data: matter } = useGetMatter(caseData?.matterId ?? '');
  const { mutateAsync: submitRunApproval, isLoading: isSubmittingRunApproval } = useSubmitRunApproval(caseId ?? '');
  const navigate = useNavigate();
  const { user } = useUserContext();

  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);

  // Determine which limit is exceeded
  const isCaseLimitExceeded =
    pageCount && organisation?.tokenApprovalLimit && pageCount > organisation.tokenApprovalLimit;
  const isMatterLimitExceeded =
    pageCount &&
    matter?.tokenLimit &&
    matter.tokenConsumption !== undefined &&
    matter.tokenConsumption + pageCount > matter.tokenLimit;

  const showCaseExceededMessage = isCaseLimitExceeded && (!isMatterLimitExceeded || !matter?.tokenLimit);
  const showMatterExceededMessage = isMatterLimitExceeded;

  // Check if this is admin approval scenario
  const requiresAdminApproval = Boolean(
    pageCount && organisation?.adminApprovalTokenLimit && pageCount > organisation.adminApprovalTokenLimit,
  );
  const isUserAuthorizedReviewer = user && user.role.permissions.includes(Permission.ApproveRuns);
  const canApprove = !requiresAdminApproval || isUserAuthorizedReviewer;

  const handleContinue = async () => {
    setIsContinueLoading(true);
    await submitRunApproval({ runId, approvalGranted: true });
    setIsContinueLoading(false);
    onApprovalGranted?.();
  };

  const handleCancel = async () => {
    setIsCancelLoading(true);
    await submitRunApproval({ runId, approvalGranted: false });
    setIsCancelLoading(false);
    setIsCancelModalOpen(false);
    onApprovalDenied?.();
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex h-fit w-full flex-col rounded-lg border border-gray-200 bg-white shadow-sm"
    >
      <div className="flex w-full items-center justify-between rounded-t-lg border-b border-gray-100 bg-gray-50 px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-50 ring-1 ring-blue-100">
            <FileCog className="h-4 w-4 text-blue-600" />
          </div>
          <h3 className="text-base font-medium text-gray-900">Processing Estimate</h3>
        </div>
        {requiresAdminApproval ? <span className="text-sm text-gray-500">(Requires Approval)</span> : ''}
      </div>

      {pageCount !== null && pageCount !== undefined && (
        <div className="flex flex-col gap-6 p-6">
          <div className="flex flex-wrap gap-3">
            <div className="flex items-center gap-3 rounded-md border border-dashed border-blue-200 bg-blue-50 px-4 py-2 shadow-sm">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-100">
                <File className="h-4 w-4 text-blue-600" />
              </div>
              <div>
                <div className="text-sm font-semibold text-gray-900">{pageCount?.toLocaleString()}</div>
                <div className="text-xs text-gray-500">Total Credits</div>
              </div>
            </div>

            {durationEstimate !== null && durationEstimate !== undefined && (
              <div className="flex items-center gap-2 rounded-md border border-dashed border-purple-200 bg-purple-50 px-4 py-2 shadow-sm">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-purple-100">
                  <FileClock className="h-4 w-4 text-purple-600" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-gray-900">
                    {[
                      (durationEstimate?.days ?? 0) > 0 &&
                        `${durationEstimate?.days} day${durationEstimate?.days !== 1 ? 's' : ''}`,
                      (durationEstimate?.hours ?? 0) > 0 &&
                        `${durationEstimate?.hours} hour${durationEstimate?.hours !== 1 ? 's' : ''}`,
                      (durationEstimate?.minutes ?? 0) > 0 &&
                        `${durationEstimate?.minutes} minute${durationEstimate?.minutes !== 1 ? 's' : ''}`,
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  </div>
                  <div className="text-xs text-gray-500">Estimated Processing Time</div>
                </div>
              </div>
            )}
          </div>

          {(showCaseExceededMessage || showMatterExceededMessage || requiresAdminApproval) && (
            <div className="border-gray-00 flex items-start gap-2 rounded-md border border-dashed bg-gray-50 p-3">
              <div className="text-xs text-gray-600">
                <p className="mb-1 font-medium text-gray-800 underline">Why am I seeing this?</p>
                <div className="flex flex-col gap-1">
                  {showMatterExceededMessage && (
                    <p data-testid="matter-limit-exceeded">
                      This case contains <b>{pageCount?.toLocaleString()}</b> {pageCount === 1 ? 'credit' : 'credits'}.
                      Processing this case would bring this matter's total consumption to{' '}
                      <b>{((matter?.tokenConsumption || 0) + (pageCount || 0)).toLocaleString()}</b> credits, which
                      exceeds the matter's limit of <b>{matter?.tokenLimit?.toLocaleString()}</b> credits. You need to
                      confirm that you want to proceed before processing can continue.
                    </p>
                  )}
                  {showCaseExceededMessage && !requiresAdminApproval && (
                    <p data-testid="case-limit-exceeded">
                      This case contains <b>{pageCount?.toLocaleString()}</b> {pageCount === 1 ? 'credit' : 'credits'}.
                      Your organisation's configuration means that you need to confirm that you want to proceed on
                      uploads of over <b>{organisation?.tokenApprovalLimit?.toLocaleString()}</b>{' '}
                      {organisation?.tokenApprovalLimit === 1 ? 'credit' : 'credits'} before processing can continue.
                    </p>
                  )}
                  {requiresAdminApproval && (
                    <p data-testid="admin-limit-exceeded">
                      This case contains <b>{pageCount?.toLocaleString()}</b> {pageCount === 1 ? 'credit' : 'credits'}.
                      Your organisation's configuration means that any uploads over{' '}
                      <b>{organisation?.adminApprovalTokenLimit?.toLocaleString()}</b>{' '}
                      {organisation?.adminApprovalTokenLimit === 1 ? 'credit' : 'credits'} require approval from
                      designated reviewers.
                    </p>
                  )}
                  <p className="mt-2 italic">
                    If you wish to adjust these limits, contact your administrator, or if you are an administrator, they
                    can be changed{' '}
                    <b
                      className="text-blue-500 underline hover:cursor-pointer hover:text-blue-600"
                      onClick={() => navigate('/app/chronos/configuration/credits')}
                    >
                      here
                    </b>
                    .
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Admin approval authorisation message */}
          {requiresAdminApproval && !canApprove && (
            <div
              data-testid="admin-limit-exceeded-cant-approve"
              className="flex items-start gap-3 rounded-md border border-dashed border-orange-200 bg-orange-50 p-3"
            >
              <ShieldAlert className="mt-0.5 h-5 w-5 text-orange-600" />
              <div className="text-xs text-orange-700">
                <p className="mb-1 font-medium text-orange-800">Admin Approval Required</p>
                <p>
                  Only users with the permissions to approve uploads can continue processing this case. Please contact
                  one of your organisation's admins or reviewers to proceed.
                </p>
              </div>
            </div>
          )}
          {requiresAdminApproval && canApprove && (
            <div
              data-testid="admin-limit-exceeded-can-approve"
              className="flex items-start gap-3 rounded-md border border-dashed border-orange-200 bg-orange-50 p-3"
            >
              <div className="text-xs text-orange-700">
                <p>
                  You have the permission to approve this upload. Please click the 'approve' button to continue
                  processing.
                </p>
              </div>
            </div>
          )}

          <div className="flex gap-8">
            <Button
              data-testid="approve-button"
              disabled={isContinueLoading || !canApprove}
              variant="primary"
              size="lg"
              className="w-full"
              onClick={handleContinue}
            >
              {isContinueLoading ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <CheckIcon />}
              <span>{requiresAdminApproval ? 'Approve' : 'Continue'}</span>
            </Button>
            <Button
              data-testid="cancel-button"
              disabled={isCancelLoading}
              variant="destructive"
              size="lg"
              className="w-full"
              onClick={() => setIsCancelModalOpen(true)}
            >
              {isCancelLoading ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <XIcon />}
              <span>Cancel</span>
            </Button>
          </div>
        </div>
      )}

      <ConfirmationModal
        isOpen={isCancelModalOpen}
        handleClose={() => setIsCancelModalOpen(false)}
        title="Cancel Processing"
        description="Are you sure you want to cancel this processing run?"
        onConfirm={handleCancel}
        isLoading={isSubmittingRunApproval}
        confirmButtonText="Cancel Processing"
        confirmButtonVariant="destructive"
      />
    </motion.div>
  );
};

export default WaitingApprovalScreen;
